import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { LoginTokenPreview } from '@kairelo/shared';
import { auth, ApiRequestError } from '../lib/api.js';
import { Icon } from '../components/Icon.tsx';
import { Button, Card, Loading, Notice } from '../components/ui.tsx';
import { useT } from '../i18n/I18n.tsx';
import { LOGIN_LINK_TTL_MINUTES } from '../lib/lifecycle.js';

/**
 * Login confirmation (concept §3).
 *
 * Loading this page performs a **read-only** preview of the token. Nothing is
 * consumed and nobody is authenticated until the person presses Continue — the
 * whole point being that email scanners and link previews routinely fetch these
 * links and execute their scripts, and none of that can complete a sign-in.
 */
export function ConfirmLogin() {
  const t = useT();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [preview, setPreview] = useState<LoginTokenPreview | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setPreview({ valid: false, reason: 'unknown' });
      return;
    }
    auth
      .previewLoginToken(token)
      .then(setPreview)
      .catch(() => setPreview({ valid: false, reason: 'unknown' }));
  }, [token]);

  if (preview === null) {
    return (
      <div className="page page--narrow">
        <Loading>{t('confirm.checking')}</Loading>
      </div>
    );
  }

  if (!preview.valid) {
    const reason =
      preview.reason === 'expired'
        ? t('confirm.expired', { minutes: LOGIN_LINK_TTL_MINUTES })
        : preview.reason === 'already_used'
          ? t('confirm.used')
          : t('confirm.invalid');
    return (
      <div className="page page--narrow">
        <div className="empty">
          <span className="empty__icon">
            <Icon name="clock" size={22} />
          </span>
          <h3>{t('confirm.unavailable')}</h3>
          <p>{reason}</p>
          <Button
            variant="primary"
            icon="mail"
            onClick={() => navigate('/login')}
            style={{ marginTop: 'var(--s-2)' }}
          >
            {t('confirm.requestNew')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="page page--narrow">
      <header className="page__header">
        <span className="tile tile--accent" aria-hidden="true">
          <Icon name="key" size={20} />
        </span>
        <div className="page__title">
          <h1>{t('confirm.title')}</h1>
          <p>{t('confirm.subtitle')}</p>
        </div>
      </header>

      <Card>
        <div className="row" style={{ gap: 'var(--s-3)', flexWrap: 'nowrap' }}>
          <span className="avatar avatar--lg" aria-hidden="true">
            <Icon name="lock" size={22} />
          </span>
          <div style={{ minWidth: 0 }}>
            {/*
              One sentence with the address inside it, not a fragment plus a
              name: word order around "as {email}" differs by language.

              `emailHint` is optional on the preview even when the token is
              valid, and the sentence is only worth saying when there is an
              address to put in it — the old markup rendered "sign in as ."
              when there was not.
            */}
            {preview.emailHint ? (
              <p style={{ margin: 0 }}>{t('confirm.as', { email: preview.emailHint })}</p>
            ) : null}
            <p className="text-sm muted" style={{ marginTop: 'var(--s-1)' }}>
              {t('confirm.explain')}
            </p>
          </div>
        </div>

        {error ? (
          <div style={{ marginTop: 'var(--s-4)' }}>
            <Notice tone="danger">{error}</Notice>
          </div>
        ) : null}

        <div style={{ marginTop: 'var(--s-5)' }}>
          <Button
            variant="primary"
            size="lg"
            icon="check"
            block
            busy={confirming}
            onClick={() => {
              setConfirming(true);
              setError(null);
              auth
                .confirm(token)
                .then(() => navigate('/app/conversations', { replace: true }))
                .catch((confirmError: unknown) => {
                  setConfirming(false);
                  setError(
                    confirmError instanceof ApiRequestError
                      ? confirmError.message
                      : t('confirm.failed'),
                  );
                });
            }}
          >
            {t('confirm.continue')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
